import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import './CookieConsent.css';

// ========================================
// TOGGLE THIS TO ENABLE/DISABLE COOKIE WALL
// Set to true to show cookie consent banner
// Set to false to hide it (for non-GDPR regions)
export const ENABLE_COOKIE_CONSENT = true;
// ========================================

const COOKIE_CONSENT_KEY = 'myt_cookie_consent';
const COOKIE_CONSENT_EXPIRY_DAYS = 365;

function CookieConsent({ translations }) {
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    if (!ENABLE_COOKIE_CONSENT) {
      return;
    }

    // Check if user has already made a choice
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!consent) {
      setShowBanner(true);
    } else {
      // Apply the stored consent
      applyConsent(consent === 'accepted');
    }
  }, []);

  // Helper function to render cookie consent message with privacy policy link
  const renderCookieMessage = () => {
    const message = translations?.cookieConsent?.message || 'We use cookies to improve your experience and for marketing purposes. By clicking "Accept", you consent to our use of cookies.';
    const privacyPolicyUrl = translations?.disclaimers?.privacyPolicyUrl || '#';
    const privacyPolicyText = translations?.cookieConsent?.privacyPolicyText || 'Privacy Policy';

    const parts = message.split('{privacyPolicyLink}');

    if (parts.length === 1) {
      // No placeholder found, return message as-is
      return message;
    }

    return (
      <>
        {parts[0]}
        <a href={privacyPolicyUrl} target="_blank" rel="noopener noreferrer">
          {privacyPolicyText}
        </a>
        {parts[1]}
      </>
    );
  };

  const applyConsent = (accepted) => {
    if (accepted) {
      // Enable tracking (Facebook Pixel is already in index.html)
      if (window.fbq) {
        window.fbq('consent', 'grant');
      }
    } else {
      // Disable tracking
      if (window.fbq) {
        window.fbq('consent', 'revoke');
      }
    }
  };

  const handleAccept = () => {
    localStorage.setItem(COOKIE_CONSENT_KEY, 'accepted');
    // Also set a cookie for cross-subdomain tracking
    const date = new Date();
    date.setTime(date.getTime() + (COOKIE_CONSENT_EXPIRY_DAYS * 24 * 60 * 60 * 1000));
    document.cookie = `${COOKIE_CONSENT_KEY}=accepted;expires=${date.toUTCString()};path=/;SameSite=Lax`;

    applyConsent(true);
    setShowBanner(false);
  };

  const handleReject = () => {
    localStorage.setItem(COOKIE_CONSENT_KEY, 'rejected');
    // Also set a cookie
    const date = new Date();
    date.setTime(date.getTime() + (COOKIE_CONSENT_EXPIRY_DAYS * 24 * 60 * 60 * 1000));
    document.cookie = `${COOKIE_CONSENT_KEY}=rejected;expires=${date.toUTCString()};path=/;SameSite=Lax`;

    applyConsent(false);
    setShowBanner(false);
  };

  if (!ENABLE_COOKIE_CONSENT || !showBanner) {
    return null;
  }

  // Use portal to render outside of parent container hierarchy
  // This ensures position:fixed works correctly on mobile
  const banner = (
    <div className="cookie-consent-overlay">
      <div className="cookie-consent-banner">
        <div className="cookie-consent-content">
          <p className="cookie-consent-text">
            {renderCookieMessage()}
          </p>
          <div className="cookie-consent-buttons">
            <button
              className="cookie-consent-btn cookie-consent-reject"
              onClick={handleReject}
            >
              {translations?.cookieConsent?.reject || 'Reject'}
            </button>
            <button
              className="cookie-consent-btn cookie-consent-accept"
              onClick={handleAccept}
            >
              {translations?.cookieConsent?.accept || 'Accept'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(banner, document.body);
}

export default CookieConsent;
